import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import CustomerApiService from "../../../CustomerApiService";

/**
 * Spotter Gamebook tab API calls (follow-up to PR #210, `feat/spotter-gamebook-tab`). Mirrors
 * `../mapping/service.ts`/`../glossary/service.ts`'s convention: thin wrappers over
 * `CustomerApiService.fetch`, bare-JSON contract, one function per endpoint — but this file also
 * exports the `useQuery`/`useMutation` HOOKS directly (the mapping/glossary screens call
 * `useQuery` inline instead), since Gamebook's queries are parameterized by season/week and
 * re-used from more than one place (`GamebookScreen` today; a future summary widget could reuse
 * them too) — see `apps/customer-frontend/CLAUDE.md`'s "Data fetching (react-query)" section for
 * why this file is the shape new server-state code here should follow.
 */

const PREFIX = "/spotter/gamebook";

/** How often `useGamebookMatchup` re-polls while a generation it triggered is still pending —
 *  same interval `apps/frontend/src/features/jobs/JobView.tsx` uses for its own job-status poll
 *  (`refetchInterval`), the existing precedent for this pattern in the monorepo. */
export const GAMEBOOK_POLL_INTERVAL_MS = 5_000;

export interface GamebookConfig {
  clubCode: string | null;
  clubName: string | null;
  currentSeason: number;
  currentWeek: string;
}

export interface GamebookPlay {
  uid: string;
  playId: number;
  quarter: number;
  gameClock: string;
  down: string | null;
  distanceToGo: string | null;
  lineOfScrimmage: string | null;
  teamOnOffense: string;
  teamOnDefense: string;
  awayScore: number;
  homeScore: number;
  yardsGainedOrLost: string | null;
  mechanicalSummary: string | null;
  penaltyType: string | null;
  penaltyTeam: string | null;
  offensivePlayElements: string | null;
  presnapElements: string | null;
  timeOfDay: string;
  playImportance: string | null;
}

export interface GamebookCompletedGame {
  gameId: string;
  season: number;
  week: string;
  away: string;
  home: string;
  location: string | null;
  plays: GamebookPlay[];
  /** When n8n's Phase 1 completion callback last confirmed a refresh finished for this game —
   *  `null` if that's never fired. See `GamebookScreen.tsx`'s "Refresh gamebook" completion
   *  detection for why this (not `status`) is what a refresh-of-a-completed-game polls for. */
  lastGeneratedAt: string | null;
}

/** One row of `GET /gamebook/weeks` — mirrors `gamebookTypes.ts`'s `GamebookWeekStatus`. */
export interface GamebookWeekStatus {
  week: string;
  generated: boolean;
  opponentCode: string | null;
  opponentName: string | null;
  home: boolean | null;
}

export interface GamebookOpponentPreview {
  opponentCode: string;
  opponentName: string;
  home: boolean;
  venue: string | null;
  kickoff: string | null;
}

export type GamebookMatchup =
  | { status: "not-configured" }
  | { status: "completed"; game: GamebookCompletedGame }
  | { status: "scheduled"; preview: GamebookOpponentPreview | null };

/** GET `/gamebook/config` — club identity + a sane default season for the season/week pickers. */
export async function getGamebookConfig(): Promise<GamebookConfig> {
  return CustomerApiService.fetch<GamebookConfig>("GET", `${PREFIX}/config`);
}

/** GET `/gamebook/weeks` — every week in a season tagged with generated/opponent status, for the
 *  week `Select`'s per-option indicator. */
export async function getGamebookWeeks(
  season: number
): Promise<GamebookWeekStatus[]> {
  const { weeks } = await CustomerApiService.fetch<{
    weeks: GamebookWeekStatus[];
  }>("GET", `${PREFIX}/weeks?season=${encodeURIComponent(season)}`);
  return weeks;
}

/** GET `/gamebook/matchup` — the ONE row for a selected season/week (completed game, scheduled
 *  preview, or "not configured"). */
export async function getGamebookMatchup(
  season: number,
  week: string
): Promise<GamebookMatchup> {
  return CustomerApiService.fetch<GamebookMatchup>(
    "GET",
    `${PREFIX}/matchup?season=${encodeURIComponent(
      season
    )}&week=${encodeURIComponent(week)}`
  );
}

/** POST `/gamebook/generate` — "Generate gamebook" (Phase 1 refresh trigger). */
export async function generateGamebook(
  season: number,
  week: string
): Promise<{ triggered: boolean }> {
  return CustomerApiService.fetch<{ triggered: boolean }>(
    "POST",
    `${PREFIX}/generate`,
    { season, week }
  );
}

export const gamebookQueryKeys = {
  config: ["spotter-gamebook-config"] as const,
  weeks: (season: number) => ["spotter-gamebook-weeks", season] as const,
  matchup: (season: number, week: string) =>
    ["spotter-gamebook-matchup", season, week] as const,
};

export function useGamebookConfig(): UseQueryResult<GamebookConfig, Error> {
  return useQuery<GamebookConfig, Error>(
    gamebookQueryKeys.config,
    getGamebookConfig,
    { staleTime: 60_000 }
  );
}

/** `staleTime` matches `useGamebookConfig` — a season's generated/opponent status doesn't change
 *  from one render to the next, only across a real "Generate gamebook" mutation, which invalidates
 *  it explicitly below rather than relying on this staleness window. */
export function useGamebookWeeks(
  season: number | undefined
): UseQueryResult<GamebookWeekStatus[], Error> {
  return useQuery<GamebookWeekStatus[], Error>(
    gamebookQueryKeys.weeks(season ?? 0),
    () => getGamebookWeeks(season as number),
    { enabled: !!season, staleTime: 60_000 }
  );
}

/** `poll: true` re-fetches this matchup every `GAMEBOOK_POLL_INTERVAL_MS` — `GamebookScreen`
 *  passes it while a "Generate gamebook" it just triggered is still pending, so the tab picks up
 *  the finished game on its own instead of the customer having to reload. Stops as soon as
 *  `poll` flips back to `false` (react-query clears the interval), which the screen does once the
 *  matchup reaches a terminal state (`status: "completed"`) or a max-wait timeout — the `/matchup`
 *  shape has no distinct "generating"/"failed" status of its own to key off of. */
export function useGamebookMatchup(
  season: number | undefined,
  week: string | undefined,
  poll = false
): UseQueryResult<GamebookMatchup, Error> {
  return useQuery<GamebookMatchup, Error>(
    gamebookQueryKeys.matchup(season ?? 0, week ?? ""),
    () => getGamebookMatchup(season as number, week as string),
    {
      enabled: !!season && !!week,
      refetchInterval: poll ? GAMEBOOK_POLL_INTERVAL_MS : undefined,
    }
  );
}

/** On success, invalidates this exact season/week's matchup query so the tab re-fetches right away
 *  and picks up the freshly-ingested game once Phase 1 finishes. Phase 1 itself runs asynchronously
 *  in n8n, so that one re-fetch usually still finds it pending — `GamebookScreen` follows up by
 *  polling `useGamebookMatchup` (`poll: true`) until it turns up completed. */
export function useGenerateGamebook(season: number, week: string) {
  const queryClient = useQueryClient();
  return useMutation(() => generateGamebook(season, week), {
    onSuccess: () => {
      queryClient.invalidateQueries(gamebookQueryKeys.matchup(season, week));
      queryClient.invalidateQueries(gamebookQueryKeys.weeks(season));
    },
  });
}
